/*
 * Copyright 2025 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import i18n.StringKey
import i18n.tr
import kotlinx.browser.document
import org.w3c.dom.HTMLElement
import recommendations.DashboardInsights
import recommendations.SavingsRecommendationCardState
import theme.ThemeColors
import kotlin.math.abs
import kotlin.math.ceil
import kotlin.math.roundToInt

/**
 * Markup for the dashboard's Recommendation ("insight") widget.
 *
 * ## Why this is a ladder and not a set of size tiers
 *
 * The grid is ROW-QUANTISED: `GridStack.init` uses `cellHeight: 60`, so the
 * widget's height is only ever a whole number of rows. Its valid heights are a
 * small finite set, and every one of them has to look full — the user's
 * complaint about the previous version was precisely that one row count left
 * dead space at the bottom while the row count below it dropped a whole
 * paragraph, with no state in between.
 *
 * So the widget does not classify its height into SMALL/MEDIUM/LARGE and render
 * one of three fixed layouts. Instead every state publishes a **ladder**: an
 * ordered list of renderings in which each rung shows strictly more than the one
 * below it — one more line of a paragraph, one more goal, the progress bars, one
 * more pool insight. The ladder is measured in the real DOM and the tallest rung
 * that fits is chosen. That gives three properties the tier version could not:
 *
 * 1. **No missing middle.** Content degrades line by line
 *    (3 → 2 → 1) and only disappears when even one line will not fit.
 * 2. **Monotonicity.** Because rungs are ordered by content and the chosen rung
 *    is "the tallest that fits", a taller widget can only ever choose the same
 *    rung or a higher one. Growing the widget can never remove content — the
 *    exact regression that produced the complaint.
 * 3. **A tight fit, honestly.** Block heights are MEASURED
 *    ([measure]), not estimated from font metrics. The previous version reserved
 *    worst-case line counts, which left ~50 px of real dead space in a wide tile
 *    where the same sentence fits on one line instead of two.
 *
 * Residual slack — always less than the cost of the next rung — is absorbed
 * first by the hero number (up to [HERO_MAX_PX]) and then by widening the gaps
 * between blocks up to [GAP_BONUS_MAX_PX], so it reads as breathing room rather
 * than a void at the bottom. Anything left over is what
 * RecommendationWidgetLayoutTest asserts is under one grid row's worth.
 *
 * ## The layout contract this file still enforces (from the previous pass)
 *
 * 1. **Nothing shrinks below its content.** Every block is `flex: 0 0 auto` and
 *    `min-height: 0` appears nowhere — that combination is what let a flex child
 *    be sized smaller than its own text, which then overflowed and painted over
 *    the block below it.
 * 2. **Nothing is centred or spread inside the column.** `justify-content:
 *    center`/`space-between` hands leftover space out as a hole in the MIDDLE.
 *    The column is top-aligned and the leftover is spent deliberately instead.
 * 3. **Content that cannot fit is never emitted.** The chosen rung is one that
 *    measured within the budget; `overflow: hidden` on the panel is only a
 *    backstop against a measurement failure, never the plan.
 *
 * ## Height range
 *
 * [MIN_GRID_ROWS] is the shortest height at which the smallest rung of every
 * state renders clean. [MAX_GRID_ROWS] is the height at which the RICHEST state
 * finally shows its whole ladder — see that constant for why it is set from the
 * richest state and not the sparsest.
 */
internal object RecWidget {

    // ------------------------------------------------------------------
    // Grid geometry — the widget's height is quantised, so say so
    // ------------------------------------------------------------------

    /** `cellHeight` passed to `GridStack.init`. Mirror it if that changes. */
    const val CELL_HEIGHT_PX = 60

    /**
     * Pixels a row loses to chrome before the content box is reached: 16 px of
     * grid margin (`margin: 8`, top + bottom) plus 32 px of widget header
     * (8 + 8 padding, a 15 px line, 1 px border).
     */
    private const val GRID_CHROME_PX = 48

    /**
     * Minimum grid rows the widget may be resized to.
     *
     * 4 rows → 192 px of content box, the shortest height at which the smallest
     * rung of all six card states renders with no overlap, no overflow and no
     * mid-widget gap, in both locales, at every tile width. Anything shorter has
     * to drop the hero number itself, at which point the widget has nothing to
     * say.
     */
    const val MIN_GRID_ROWS = 4

    /**
     * Maximum grid rows the widget may be resized to.
     *
     * Set by the RICHEST state, not the sparsest. The rule is **no content the
     * ladder can produce may be unreachable**: whatever height the fullest
     * rendering needs, the user must be able to drag to it.
     *
     * The previous value (6) was chosen the other way round — it was the tallest
     * height at which even the sparsest states (`EarlyMonth`, a funded-goals
     * surplus, whose ladder is a paragraph, a totals strip and the pool) still
     * filled the tile. That capped the widget at what the emptiest month can
     * fill, which permanently hid content the ladder renders in a full one: an
     * over-budget month stopped at the driver row with its detail line clipped
     * and the second pool insight never reachable at all.
     *
     * 10 rows is where `HasAllocations` — five goals with progress bars and
     * their captions, plus both pool insights at two lines of title and two of
     * detail, the tallest ladder any state has — is finally whole in a narrow
     * (280 px) tile. It is not a remembered number: RecommendationWidgetLayoutTest
     * checks that every state is whole HERE and that at least one is not whole
     * one row lower, bisecting with [rowsToExhaustLadder] to confirm the height
     * that hold-out needs. The cap therefore follows the content.
     *
     * The deliberate consequence: dragged near this height in a SPARSE month the
     * tile will show trailing slack, because there is genuinely nothing more to
     * say. That is honest and must not be "fixed" by lowering the cap again —
     * slack when content is exhausted costs the user nothing, hiding content
     * that exists costs them the content.
     */
    const val MAX_GRID_ROWS = 10

    /** Content-box height (`clientHeight` of `#widget-content-…`) at [rows] rows. */
    fun contentHeightForRows(rows: Int): Int = rows * CELL_HEIGHT_PX - GRID_CHROME_PX

    /** The row count a measured content box came from — the inverse of [contentHeightForRows]. */
    fun rowsForContentHeight(contentHeightPx: Int): Int =
        ((contentHeightPx + GRID_CHROME_PX + CELL_HEIGHT_PX / 2) / CELL_HEIGHT_PX).coerceAtLeast(1)

    // ------------------------------------------------------------------
    // Panel geometry
    // ------------------------------------------------------------------

    /** `#widget-content-…` has 6 px of padding, so the panel is that much shorter/narrower. */
    private const val CONTENT_PAD_PX = 12

    /** Panel padding: 12 px top + bottom, 14 px left + right. */
    private const val PANEL_PAD_Y_PX = 24
    private const val PANEL_PAD_X_PX = 28

    /** Base gap between the panel's direct children. */
    private const val GAP_PX = 9

    /**
     * How much the base gap may grow to soak up slack the hero could not take.
     * 9 → 23 px is still a gap and not a rip; the test's mid-widget-gap ceiling
     * is well clear of it.
     */
    private const val GAP_BONUS_MAX_PX = 14

    /**
     * Pixels held back from the budget so a rung is never chosen that fits to the
     * pixel. The probe measures the same markup at the same width in the same
     * font, so it is accurate — but "accurate" and "identical after subpixel
     * rounding at a non-integer device pixel ratio" are different claims, and the
     * failure mode of being 1 px out is a clipped CTA. Two pixels of nothing at
     * the bottom is a cheaper mistake.
     */
    private const val FIT_MARGIN_PX = 2

    private const val HERO_MIN_PX = 26
    private const val HERO_MAX_PX = 40

    /** Hero font sizes tried, largest first — the first that fits wins. */
    private val HERO_STEPS_PX = (HERO_MAX_PX downTo HERO_MIN_PX step 2).toList()

    // ------------------------------------------------------------------
    // Measurement
    // ------------------------------------------------------------------

    /**
     * Offscreen box the candidate rungs are laid out in.
     *
     * Appended to `<body>` deliberately: the font stack the widget is measured in
     * then IS the page's, rather than a copy of it that can drift. It is emptied
     * as soon as the heights are read — the candidates carry `#viewBudgetBtn`,
     * `#createGoalBtn` and `.goal-link`, and duplicates of those must not be
     * sitting in the document when the dashboard wires up its click handlers.
     */
    private var probeHost: HTMLElement? = null

    private class Measured(val heightPx: Int, val childCount: Int)

    /**
     * Lay every candidate out as the panel's child column at [widthPx] and read
     * back its height and child count. One DOM write and one layout flush for
     * the whole batch.
     *
     * Returns null when there is no document to measure in; callers then fall
     * back to the smallest rung, which is the one that cannot overflow.
     */
    private fun measure(widthPx: Int, candidates: List<String>): List<Measured>? {
        if (candidates.isEmpty()) return emptyList()
        val body = document.body ?: return null
        val host = probeHost ?: (document.createElement("div") as HTMLElement).also { probeHost = it }
        host.setAttribute("aria-hidden", "true")
        host.setAttribute(
            "style",
            "position: absolute; left: -20000px; top: 0; visibility: hidden; pointer-events: none;",
        )
        if (host.parentElement == null) body.appendChild(host)
        host.innerHTML = candidates.joinToString("") { children ->
            """<div data-rec-probe style="box-sizing: border-box; width: ${widthPx}px; display: flex; flex-direction: column; gap: ${GAP_PX}px;">$children</div>"""
        }
        val probes = host.querySelectorAll("[data-rec-probe]")
        val out = ArrayList<Measured>(candidates.size)
        for (i in 0 until probes.length) {
            val el = probes.item(i) as HTMLElement
            out += Measured(ceil(el.getBoundingClientRect().height).toInt(), el.children.length)
        }
        host.innerHTML = ""
        return out
    }

    // ------------------------------------------------------------------
    // Rungs
    // ------------------------------------------------------------------

    /**
     * One candidate rendering of the panel's children.
     *
     * [content] is the rung's content, counted: how many goal rows, how many
     * lines of the guidance paragraph, how many insights. A ladder is valid only
     * if every rung DOMINATES the one below it — no counter ever goes down. That
     * is the machine-checkable form of "growing the widget may only add or
     * expand", and it is asserted directly rather than inferred from pixels.
     * A rung may spend more height on the SAME content (a chip grid becoming a
     * list, an allocation row growing a progress bar is counted as detail), and
     * that is allowed: the counters stay put.
     */
    private class Rung(
        val bodyHtml: String,
        val insightsHtml: String,
        val content: Map<String, Int>,
    )

    private fun rung(bodyHtml: String, vararg content: Pair<String, Int>) =
        Rung(bodyHtml, "", content.toMap())

    // ------------------------------------------------------------------
    // Pieces
    // ------------------------------------------------------------------

    /** Whole dollars with thousands separators, matching the dashboard's other totals. */
    fun dollars(amount: Double): String =
        "${'$'}${abs(amount).roundToInt().asDynamic().toLocaleString()}"

    /** CSS for a paragraph that can never grow past [lines] lines. */
    private fun clamp(lines: Int): String =
        "display: -webkit-box; -webkit-line-clamp: $lines; -webkit-box-orient: vertical; overflow: hidden;"

    private fun paragraph(color: String, fontSize: String, lineHeight: String, lines: Int, text: String): String = """
        <div style="
            flex: 0 0 auto;
            color: $color; font-size: $fontSize; line-height: $lineHeight;
            ${clamp(lines)}
        ">$text</div>
    """

    /** The widget's single hero number plus the label that names it. */
    private fun hero(t: ThemeColors, amount: String, label: String, color: String, heroPx: Int): String = """
        <div style="flex: 0 0 auto; display: flex; align-items: baseline; gap: 8px; flex-wrap: wrap;">
            <span style="color: $color; font-size: ${heroPx}px; font-weight: 700; line-height: 1.1; white-space: nowrap;">$amount</span>
            <span style="color: ${t.textSecondary}; font-size: ${DashType.LABEL};">$label</span>
        </div>
    """

    /** Income / expenses so far, side by side. */
    private fun monthStrip(t: ThemeColors, income: Double, expenses: Double): String {
        fun cell(label: String, value: String, color: String) = """
            <div style="flex: 1 1 0; min-width: 0;">
                <div style="color: ${t.textSecondary}; font-size: ${DashType.CAPTION};">$label</div>
                <div style="color: $color; font-size: ${DashType.BODY}; font-weight: ${DashType.SEMI}; white-space: nowrap;">$value</div>
            </div>
        """
        return """
            <div style="flex: 0 0 auto; display: flex; gap: 14px; padding-top: 8px; border-top: 1px solid ${t.border};">
                ${cell(StringKey.CHART_INCOME.tr(), dollars(income), t.incomeGreen)}
                ${cell(StringKey.CHART_EXPENSES.tr(), dollars(expenses), t.expenseRed)}
            </div>
        """
    }

    /**
     * One goal's slice of this month's room to save, flattened so the markup
     * needs no SavingsGoal (and so a test can build one by hand).
     */
    data class GoalSlice(
        val goalId: String,
        val name: String,
        val iconEmoji: String,
        val amount: Double,
        val progressPercent: Int,
        val timeLeft: String,
    )

    /**
     * One allocation row at one of three detail levels: 0 = name and amount,
     * 1 = plus the progress bar, 2 = plus its "38% · 8 months left" caption.
     * Three levels rather than two so the whole list thickens in ~10 px steps
     * instead of one ~90 px jump the tile has no intermediate height for.
     *
     * The bar comes before the caption because the bar and the caption share a
     * line: a caption-first level would cost the full line and leave the bar
     * free, which is two levels with a 90 px gap between them, not three.
     */
    private fun goalRow(t: ThemeColors, a: GoalSlice, detail: Int): String {
        val nameCell = """
            <span class="goal-link" data-goal-id="${a.goalId}" style="
                display: inline-flex; align-items: center; gap: 6px;
                color: ${t.accent}; cursor: pointer; font-size: ${DashType.BODY};
                min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
            " title="${a.name}">${CategoryIconSvg.renderGoalIcon(a.iconEmoji, size = 13, color = t.accent)}${a.name}</span>
        """
        val amountCell = """
            <span style="color: ${t.textPrimary}; font-size: ${DashType.BODY}; font-weight: ${DashType.SEMI}; white-space: nowrap;">${dollars(a.amount)}</span>
        """
        val topRow = """
            <div style="flex: 0 0 auto; display: flex; align-items: baseline; justify-content: space-between; gap: 8px; min-width: 0;">
                $nameCell
                $amountCell
            </div>
        """
        if (detail <= 0) return topRow
        val caption = if (detail >= 2) {
            """<span style="color: ${t.textSecondary}; font-size: ${DashType.CAPTION}; white-space: nowrap;">${a.progressPercent}% &middot; ${a.timeLeft}</span>"""
        } else ""
        // max-width so a full-width tile draws a progress bar and not a 900 px
        // pipe — which is what it looked like when this rung was screenshotted.
        return """
            <div style="flex: 0 0 auto; display: flex; flex-direction: column; gap: 4px; min-width: 0;">
                $topRow
                <div style="flex: 0 0 auto; display: flex; align-items: center; gap: 8px;">
                    <div style="flex: 1 1 0; max-width: 240px; height: 5px; background: ${t.border}; border-radius: 3px; overflow: hidden;">
                        <div style="width: ${a.progressPercent}%; height: 100%; background: ${t.accent}; border-radius: 3px;"></div>
                    </div>
                    $caption
                </div>
            </div>
        """
    }

    /** How many lines of an insight's title and detail a rung shows. */
    private class InsightLines(val title: Int, val detail: Int)

    /**
     * Insights #2/#3 from the shared pool, divider-separated under the primary.
     * [lines] is per-insight and parallel to [insights]: a detail of 0 omits the
     * supporting sentence entirely, which is the rung a tile takes when it has
     * room for the headline but not the explanation.
     */
    private fun insightsBlock(
        t: ThemeColors,
        insights: List<DashboardInsights.Insight>,
        lines: List<InsightLines>,
    ): String {
        if (insights.isEmpty()) return ""
        val rows = insights.mapIndexed { index, insight ->
            val spec = lines[index]
            val color = when (insight.tone) {
                DashboardInsights.InsightTone.NEGATIVE -> t.expenseRed
                DashboardInsights.InsightTone.POSITIVE -> t.incomeGreen
                DashboardInsights.InsightTone.NEUTRAL -> t.textPrimary
            }
            val titleStyle = "cursor: pointer; color: $color; font-size: ${DashType.BODY}; " +
                "font-weight: ${DashType.SEMI}; line-height: 1.3; ${clamp(spec.title)}"
            // GOAL_BEHIND reuses the existing .goal-link hook so its click target
            // needs no new handler; the other actions go through
            // data-insight-action (wired in setupRecommendationEventListeners).
            val action = insight.action
            val attrs = when {
                action == DashboardInsights.InsightAction.OPEN_GOAL ->
                    """class="goal-link" data-goal-id="${insight.subject ?: ""}" style="$titleStyle""""
                action != null ->
                    """data-insight-action="${insightActionAttr(action)}" style="$titleStyle""""
                else ->
                    """style="color: $color; font-size: ${DashType.BODY}; font-weight: ${DashType.SEMI}; line-height: 1.3; ${clamp(spec.title)}""""
            }
            val detail = if (spec.detail > 0) {
                """<span style="color: ${t.textSecondary}; font-size: ${DashType.LABEL}; line-height: 1.4; ${clamp(spec.detail)}">${insight.detail.resolve()}</span>"""
            } else ""
            """
            <div style="flex: 0 0 auto; display: flex; flex-direction: column; gap: 1px; min-width: 0; ${if (index > 0) "padding-top: 8px; border-top: 1px solid ${t.border};" else ""}">
                <span $attrs>${insight.title.resolve()}</span>
                $detail
            </div>
            """
        }.joinToString("")
        return """
            <div style="flex: 0 0 auto; display: flex; flex-direction: column; gap: 8px; padding-top: 8px; border-top: 1px solid ${t.border};">
                <div style="color: ${t.textSecondary}; font-size: ${DashType.CAPTION}; text-transform: uppercase; letter-spacing: 0.06em;">${StringKey.INSIGHT_ALSO_WORTH_NOTING.tr()}</div>
                $rows
            </div>
        """
    }

    private fun insightActionAttr(action: DashboardInsights.InsightAction): String = when (action) {
        DashboardInsights.InsightAction.REVIEW_BUDGET -> "review-budget"
        DashboardInsights.InsightAction.OPEN_TRANSACTIONS -> "open-transactions"
        DashboardInsights.InsightAction.OPEN_GOAL -> "open-goal"
    }

    /**
     * The pool's rungs, appended above the fully-expanded body.
     *
     * Each insight arrives headline-first and then explains itself, so the block
     * grows in ~18 px steps: title on one line, title on two, plus one line of
     * detail, plus the second. Insights already shown stay at full size — a rung
     * may never take something back.
     */
    private fun insightRungs(t: ThemeColors, insights: List<DashboardInsights.Insight>): List<Rung> {
        if (insights.isEmpty()) return emptyList()
        val ladder = listOf(
            InsightLines(1, 0),
            InsightLines(2, 0),
            InsightLines(2, 1),
            InsightLines(2, 2),
        )
        val full = InsightLines(2, 2)
        val out = mutableListOf<Rung>()
        for (n in 1..insights.size) {
            for (step in ladder) {
                val spec = List(n - 1) { full } + step
                out += Rung(
                    bodyHtml = "",
                    insightsHtml = insightsBlock(t, insights.take(n), spec),
                    content = mapOf(
                        "insights" to n,
                        "insightTitleLines" to spec.sumOf { it.title },
                        "insightDetailLines" to spec.sumOf { it.detail },
                    ),
                )
            }
        }
        return out
    }

    // ------------------------------------------------------------------
    // Ladders — one per card state
    // ------------------------------------------------------------------

    /** HasAllocations: the room to save, plus who gets it. */
    private fun allocationRungs(
        t: ThemeColors,
        heroPx: Int,
        available: Double,
        allocations: List<GoalSlice>,
        monthLabel: String,
    ): List<Rung> {
        val heroHtml = hero(
            t, dollars(available), StringKey.INSIGHT_AVAILABLE_TO_SAVE_LABEL.tr(monthLabel), t.incomeGreen, heroPx,
        )
        val header =
            """<div style="flex: 0 0 auto; color: ${t.textSecondary}; font-size: ${DashType.CAPTION}; text-transform: uppercase; letter-spacing: 0.06em;">${StringKey.DASHBOARD_RECOMMENDED_ALLOCATION_HEADER.tr()}</div>"""

        fun list(count: Int, detail: Int): String {
            val gap = if (detail > 0) 8 else 4
            val rows = allocations.take(count).joinToString("") { goalRow(t, it, detail) }
            return """<div style="flex: 0 0 auto; display: flex; flex-direction: column; gap: ${gap}px;">$rows</div>"""
        }

        val total = allocations.size.coerceAtLeast(1)
        val out = mutableListOf<Rung>()
        // First every goal, one at a time, at the plainest density; only once all
        // of them are on screen does the list thicken. Showing five goals beats
        // showing three of them with progress bars.
        for (n in 1..total) {
            out += rung(heroHtml + header + list(n, 0), "hero" to 1, "goalRows" to n, "goalDetail" to 0)
        }
        for (detail in 1..2) {
            out += rung(
                heroHtml + header + list(total, detail),
                "hero" to 1, "goalRows" to total, "goalDetail" to detail,
            )
        }
        return out
    }

    /** A hero amount plus the month's income/expenses, when that is all there is to say. */
    private fun heroStripRungs(
        t: ThemeColors,
        heroPx: Int,
        amount: Double,
        label: String,
        color: String,
        month: MonthTotals?,
    ): List<Rung> {
        val heroHtml = hero(t, dollars(amount), label, color, heroPx)
        val out = mutableListOf(rung(heroHtml, "hero" to 1, "strip" to 0))
        if (month != null) {
            out += rung(
                heroHtml + monthStrip(t, month.income, month.expenses),
                "hero" to 1, "strip" to 1,
            )
        }
        return out
    }

    /** SpendLessNudge with no room: the shortfall, what drove it, and the CTA. */
    private fun overBudgetRungs(
        t: ThemeColors,
        heroPx: Int,
        available: Double,
        month: MonthTotals?,
        driver: Pair<String, Double>?,
        monthLabel: String,
    ): List<Rung> {
        val heroHtml = hero(
            t, dollars(available), StringKey.INSIGHT_OVER_BUDGET_LABEL.tr(monthLabel), t.expenseRed, heroPx,
        )
        val driverHtml = driver?.let {
            """
            <div style="flex: 0 0 auto; display: flex; align-items: baseline; justify-content: space-between; gap: 8px; padding-top: 8px; border-top: 1px solid ${t.border};">
                <span style="color: ${t.textSecondary}; font-size: ${DashType.BODY}; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;" title="${it.first}">${it.first}</span>
                <span style="color: ${t.expenseRed}; font-size: ${DashType.BODY}; font-weight: ${DashType.SEMI}; white-space: nowrap;">+${dollars(it.second)}</span>
            </div>
            """
        }
        val stripHtml = month?.let { monthStrip(t, it.income, it.expenses) }
        // The hero already states the shortfall, so the guidance never repeats
        // the number — otherwise the card restates itself, the same defect the
        // insight pool de-duplicates against.
        fun guidance(lines: Int) = paragraph(
            t.textSecondary, DashType.LABEL, "1.45", lines, StringKey.INSIGHT_OVER_BUDGET_GUIDANCE.tr(),
        )

        // Order = priority. What drove the overspend and the month's totals are
        // specific to this user; the guidance paragraph is generic, so it is last
        // in and first out — but it now arrives one line at a time instead of
        // appearing whole or not at all.
        var body = heroHtml
        var driverN = 0
        var stripN = 0
        val out = mutableListOf(rung(body, "hero" to 1, "driver" to 0, "strip" to 0, "guidanceLines" to 0))
        if (driverHtml != null) {
            body += driverHtml
            driverN = 1
            out += rung(body, "hero" to 1, "driver" to 1, "strip" to 0, "guidanceLines" to 0)
        }
        if (stripHtml != null) {
            body += stripHtml
            stripN = 1
            out += rung(body, "hero" to 1, "driver" to driverN, "strip" to 1, "guidanceLines" to 0)
        }
        for (lines in 1..3) {
            out += rung(
                body + guidance(lines),
                "hero" to 1, "driver" to driverN, "strip" to stripN, "guidanceLines" to lines,
            )
        }
        return out
    }

    /**
     * EarlyMonth / ExpensesBeforeIncome / "spending exactly what you earn":
     * explain why there is nothing to recommend, and show what HAS landed.
     */
    private fun nudgeRungs(t: ThemeColors, message: String, month: MonthTotals?): List<Rung> {
        fun msg(lines: Int) = paragraph(t.textSecondary, DashType.BODY, "1.5", lines, message)
        val stripHtml = month?.let { monthStrip(t, it.income, it.expenses) }
        val out = mutableListOf(
            rung(msg(1), "messageLines" to 1, "strip" to 0),
            rung(msg(2), "messageLines" to 2, "strip" to 0),
        )
        // The totals are data and the message is boilerplate, so the strip comes
        // in before the third and fourth line of prose.
        val strip = stripHtml ?: ""
        val stripN = if (stripHtml != null) 1 else 0
        if (stripHtml != null) {
            out += rung(msg(2) + stripHtml, "messageLines" to 2, "strip" to 1)
        }
        for (lines in 3..4) {
            out += rung(msg(lines) + strip, "messageLines" to lines, "strip" to stripN)
        }
        return out
    }

    /** NoGoals: an invitation, with the starter goals as something to pick from. */
    private fun noGoalsRungs(t: ThemeColors): List<Rung> {
        val suggestions = listOf(
            StringKey.DASHBOARD_EMERGENCY_FUND.tr() to StringKey.DASHBOARD_EMERGENCY_FUND_DESC.tr(),
            StringKey.DASHBOARD_VACATION.tr() to StringKey.DASHBOARD_VACATION_DESC.tr(),
            StringKey.DASHBOARD_HOME_DOWN_PAYMENT.tr() to StringKey.DASHBOARD_HOME_DOWN_PAYMENT_DESC.tr(),
            StringKey.DASHBOARD_RETIREMENT.tr() to StringKey.DASHBOARD_RETIREMENT_DESC.tr(),
        )
        fun invite(lines: Int) = paragraph(
            t.textSecondary, DashType.LABEL, "1.45", lines, StringKey.DASHBOARD_GOAL_SUGGESTION.tr(),
        )
        // All four starter goals are present at every rung — only how much room
        // each gets changes. A 2×2 chip grid is the densest form, a one-per-line
        // list the same names with air around them, and the top rung adds the
        // one-line "what this is for" under each.
        val chips = suggestions.joinToString("") { (name, _) ->
            """
            <div style="
                background: ${t.chipUnselectedBg}; border: 1px solid ${t.chipUnselectedBorder};
                border-radius: 8px; padding: 5px 9px; color: ${t.textPrimary};
                font-size: ${DashType.LABEL}; font-weight: ${DashType.SEMI};
                overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
            ">$name</div>
            """
        }.let { """<div style="flex: 0 0 auto; display: grid; grid-template-columns: 1fr 1fr; gap: 6px;">$it</div>""" }

        fun rows(descLines: Int) = suggestions.joinToString("") { (name, desc) ->
            val description = if (descLines > 0) {
                """<span style="color: ${t.textSecondary}; font-size: ${DashType.LABEL}; line-height: 1.4; ${clamp(descLines)}">$desc</span>"""
            } else ""
            """
            <div style="flex: 0 0 auto; display: flex; flex-direction: column; gap: 1px; min-width: 0;">
                <span style="color: ${t.textPrimary}; font-size: ${DashType.BODY}; font-weight: ${DashType.SEMI};">$name</span>
                $description
            </div>
            """
        }.let { """<div style="flex: 0 0 auto; display: flex; flex-direction: column; gap: 7px;">$it</div>""" }

        val out = mutableListOf<Rung>()
        // The floor drops the invitation, not the goals: four named starter goals
        // under a "Savings Recommendation" heading next to a "Create a goal"
        // button say the same thing the sentence does, and the sentence is the
        // only block in this card that carries no information.
        out += rung(chips, "inviteLines" to 0, "goalIdeas" to 4, "goalIdeaDescLines" to 0)
        for (lines in 1..3) {
            out += rung(invite(lines) + chips, "inviteLines" to lines, "goalIdeas" to 4, "goalIdeaDescLines" to 0)
        }
        out += rung(invite(3) + rows(0), "inviteLines" to 3, "goalIdeas" to 4, "goalIdeaDescLines" to 0)
        out += rung(invite(3) + rows(1), "inviteLines" to 3, "goalIdeas" to 4, "goalIdeaDescLines" to 1)
        return out
    }

    // ------------------------------------------------------------------
    // Shell
    // ------------------------------------------------------------------

    private fun headerHtml(t: ThemeColors): String = """
        <div style="flex: 0 0 auto; display: flex; align-items: center; gap: 8px;">
            <span style="
                width: 22px; height: 22px; flex-shrink: 0;
                background: ${if (t.isDark) "rgba(0,217,255,0.12)" else "rgba(0,151,167,0.12)"};
                border-radius: 50%;
                display: flex; align-items: center; justify-content: center;
                font-size: ${DashType.CAPTION};
            ">&#128161;</span>
            <h3 style="margin: 0; color: ${t.accent}; font-size: ${DashType.TITLE}; font-weight: ${DashType.SEMI};">${StringKey.DASHBOARD_SAVINGS_RECOMMENDATION.tr()}</h3>
        </div>
    """

    private fun ctaHtml(t: ThemeColors, id: String, label: String): String = """
        <button id="$id" style="
            flex: 0 0 auto;
            align-self: flex-start;
            padding: 6px 14px;
            border: 1px solid ${t.accent};
            border-radius: 6px;
            background: transparent;
            color: ${t.accent};
            cursor: pointer;
            font-size: ${DashType.LABEL};
            font-weight: 500;
            transition: all 0.2s;
        " onmouseover="this.style.background='rgba(0,217,255,0.1)'" onmouseout="this.style.background='transparent'">$label</button>
    """

    /**
     * The panel around the chosen rung's children.
     *
     * `overflow: hidden` is the backstop, not the plan: the chosen rung measured
     * within the budget. Plain flex and the DashType scale — no container
     * queries, no per-widget font clamps.
     */
    private fun shell(children: String, gapPx: Int): String = """
        <div id="recommendationPanel" style="
            width: 100%; height: 100%;
            box-sizing: border-box;
            padding: 12px 14px;
            display: flex; flex-direction: column; gap: ${gapPx}px;
            overflow: hidden;
        ">
            $children
        </div>
    """

    // ------------------------------------------------------------------
    // Entry point
    // ------------------------------------------------------------------

    /** The month's income and realized expenses, or null when the month has none. */
    data class MonthTotals(val income: Double, val expenses: Double)

    /** Everything the widget draws, gathered by the dashboard. */
    class Data(
        val state: SavingsRecommendationCardState,
        /**
         * The display month, sentence-form ("July", "juillet"). Every line that
         * scopes a number to a month names it — the card follows the dashboard's
         * display month, which is usually not the calendar's current one.
         */
        val monthLabel: String,
        val available: Double,
        val allocations: List<GoalSlice>,
        val month: MonthTotals?,
        /** The biggest over-plan category and its overage, when there is one. */
        val overBudgetDriver: Pair<String, Double>?,
        /** Insights #2/#3 from the shared pool; the ladder decides how many fit. */
        val insights: List<DashboardInsights.Insight>,
    )

    /** What the widget decided to draw — the assertable form of the layout. */
    data class Plan(
        /** Which rung of [rungCount] was chosen, counting from the smallest. */
        val rungIndex: Int,
        val rungCount: Int,
        /** Hero font after it absorbed what it could of the leftover. */
        val heroFontPx: Int,
        /** Child gap after the rest of the leftover was spread across it. */
        val gapPx: Int,
        /** Empty space left below the last block. The number this pass exists to shrink. */
        val slackPx: Int,
        val content: Map<String, Int>,
    )

    class Rendered(val html: String, val plan: Plan)

    /** The CTA a given card state offers, if any: element id to label. */
    private fun cta(state: SavingsRecommendationCardState, available: Double): Pair<String, String>? = when {
        state is SavingsRecommendationCardState.NoGoals ->
            "createGoalBtn" to StringKey.DASHBOARD_CREATE_GOAL.tr()
        state is SavingsRecommendationCardState.SpendLessNudge && available <= 0.0 ->
            "viewBudgetBtn" to StringKey.DASHBOARD_VIEW_BUDGET.tr()
        else -> null
    }

    /** Whether this state has a hero number that can absorb slack by growing. */
    private fun hasHero(data: Data): Boolean = when (val state = data.state) {
        is SavingsRecommendationCardState.HasAllocations -> true
        is SavingsRecommendationCardState.SpendLessNudge -> state.availableAmount != 0.0
        else -> false
    }

    private fun bodyRungs(t: ThemeColors, data: Data, heroPx: Int): List<Rung> =
        when (val state = data.state) {
            is SavingsRecommendationCardState.NoGoals ->
                // No available/over-budget headline for a user with no goals —
                // the card is purely an invitation to create one.
                noGoalsRungs(t)
            is SavingsRecommendationCardState.EarlyMonth ->
                nudgeRungs(t, StringKey.DASHBOARD_EARLY_MONTH_HINT.tr(), data.month)
            SavingsRecommendationCardState.NoMonthData ->
                nudgeRungs(t, StringKey.DASHBOARD_NO_MONTH_DATA.tr(data.monthLabel), data.month)
            is SavingsRecommendationCardState.ExpensesBeforeIncome ->
                nudgeRungs(t, StringKey.DASHBOARD_EXPENSES_BEFORE_INCOME.tr(), data.month)
            is SavingsRecommendationCardState.HasAllocations ->
                allocationRungs(t, heroPx, data.available, data.allocations, data.monthLabel)
            is SavingsRecommendationCardState.SpendLessNudge -> when {
                state.availableAmount > 0.0 ->
                    // Surplus but nothing left to allocate (goals funded) → just show it.
                    heroStripRungs(
                        t, heroPx, state.availableAmount,
                        StringKey.INSIGHT_AVAILABLE_TO_SAVE_LABEL.tr(data.monthLabel), t.incomeGreen, data.month,
                    )
                state.availableAmount < 0.0 ->
                    overBudgetRungs(t, heroPx, state.availableAmount, data.month, data.overBudgetDriver, data.monthLabel)
                else ->
                    // Spending exactly what you earn is not a shortfall — no red
                    // hero number, just the nudge that explains the situation.
                    nudgeRungs(t, StringKey.DASHBOARD_NUDGE_SPEND_LESS.tr(data.monthLabel), data.month)
            }
        }

    /**
     * The whole ladder at a given hero size: the body's rungs, then the pool's
     * rungs stacked on top of the fully-expanded body.
     */
    private fun ladder(t: ThemeColors, data: Data, heroPx: Int): List<Rung> {
        val body = bodyRungs(t, data, heroPx)
        val top = body.last()
        return body + insightRungs(t, data.insights).map {
            Rung(top.bodyHtml, it.insightsHtml, top.content + it.content)
        }
    }

    /**
     * Render the whole panel for a content box [contentHeightPx] tall and
     * [contentWidthPx] wide (the `clientHeight` / `clientWidth` of
     * `#widget-content-recommendation`, padding included).
     */
    fun render(t: ThemeColors, contentHeightPx: Int, contentWidthPx: Int, data: Data): String =
        build(t, contentHeightPx, contentWidthPx, data).html

    fun build(t: ThemeColors, contentHeightPx: Int, contentWidthPx: Int, data: Data): Rendered {
        val ctaPair = cta(data.state, data.available)
        val cta = ctaPair?.let { ctaHtml(t, it.first, it.second) } ?: ""
        val header = headerHtml(t)
        fun children(r: Rung) = header + r.bodyHtml + cta + r.insightsHtml

        val innerWidth = (contentWidthPx - CONTENT_PAD_PX - PANEL_PAD_X_PX).coerceAtLeast(120)
        val avail = (contentHeightPx - CONTENT_PAD_PX - PANEL_PAD_Y_PX).coerceAtLeast(40)
        val budget = (avail - FIT_MARGIN_PX).coerceAtLeast(40)

        // Pass one: pick the rung, with the hero at its base size so that growing
        // the hero afterwards can never change what the widget shows (which would
        // feed back into the slack the hero is sized from, and oscillate).
        val base = ladder(t, data, HERO_MIN_PX)
        val baseHeights = measure(innerWidth, base.map { children(it) })
        val index = baseHeights?.indexOfLast { it.heightPx <= budget }?.coerceAtLeast(0) ?: 0
        var chosen = base[index]
        var heroPx = HERO_MIN_PX
        var used = baseHeights?.get(index)?.heightPx ?: budget
        var childCount = baseHeights?.get(index)?.childCount ?: 1

        // Pass two: hand the hero whatever room the chosen rung left over, as far
        // as the cap. Measured rather than derived from a cost-per-pixel estimate,
        // because a grown hero can wrap its label and that is a step, not a slope.
        if (baseHeights != null && hasHero(data) && used < budget) {
            val heights = measure(innerWidth, HERO_STEPS_PX.map { children(ladder(t, data, it)[index]) })
            val pick = heights?.indexOfFirst { it.heightPx <= budget } ?: -1
            if (heights != null && pick >= 0) {
                heroPx = HERO_STEPS_PX[pick]
                chosen = ladder(t, data, heroPx)[index]
                used = heights[pick].heightPx
                childCount = heights[pick].childCount
            }
        }

        // Whatever is still left is spread across the gaps rather than pooled at
        // the bottom: the same content, breathing, instead of content plus a void.
        val gapBonus = if (childCount > 1) {
            ((budget - used) / (childCount - 1)).coerceIn(0, GAP_BONUS_MAX_PX)
        } else 0
        val usedWithGaps = used + gapBonus * (childCount - 1)

        return Rendered(
            html = shell(children(chosen), GAP_PX + gapBonus),
            plan = Plan(
                rungIndex = index,
                rungCount = base.size,
                heroFontPx = heroPx,
                gapPx = GAP_PX + gapBonus,
                slackPx = (avail - usedWithGaps).coerceAtLeast(0),
                content = chosen.content,
            ),
        )
    }

    // ------------------------------------------------------------------
    // Test seam
    // ------------------------------------------------------------------

    /**
     * The ladder's content bags, in order — the sequence a growing widget walks
     * up. Exposed so the test can assert the ladder itself is monotone (no rung
     * takes anything back), independently of any particular height.
     */
    internal fun ladderContent(t: ThemeColors, data: Data): List<Map<String, Int>> =
        ladder(t, data, HERO_MIN_PX).map { it.content }

    /**
     * The shortest row count at which [data] renders its ENTIRE ladder at
     * [contentWidthPx] — the height past which this state has nothing left to
     * say, and below which some of what it could say is unreachable.
     *
     * Measured, not estimated: it runs the real [build] against the real probe,
     * so it tracks the content. The chosen rung is "the tallest that fits",
     * which is non-decreasing in height, so the answer is bisected rather than
     * walked. [searchTo] bounds the search; a state that is still not whole
     * there returns [searchTo] and the caller sees a cap it cannot satisfy.
     *
     * This is where [MAX_GRID_ROWS] comes from: its maximum over every state,
     * tile width and locale.
     */
    internal fun rowsToExhaustLadder(
        t: ThemeColors,
        data: Data,
        contentWidthPx: Int,
        searchTo: Int = 24,
    ): Int {
        fun whole(rows: Int): Boolean {
            val plan = build(t, contentHeightForRows(rows), contentWidthPx, data).plan
            return plan.rungIndex >= plan.rungCount - 1
        }
        if (whole(MIN_GRID_ROWS)) return MIN_GRID_ROWS
        if (!whole(searchTo)) return searchTo
        var below = MIN_GRID_ROWS // known incomplete
        var atOrAbove = searchTo // known complete
        while (atOrAbove - below > 1) {
            val mid = (below + atOrAbove) / 2
            if (whole(mid)) atOrAbove = mid else below = mid
        }
        return atOrAbove
    }
}
